import { useState } from "react";
import { motion } from "framer-motion";
import { Mail, Phone, MapPin } from "lucide-react";
import { toast } from "sonner";
import GoldButton from "./GoldButton";

const inputCls =
  "w-full border-b border-white/15 bg-transparent py-3 text-base font-light text-[#F5F5F0] outline-none transition-colors duration-400 placeholder:text-[#8A8A84] focus:border-[#D4AF37]";

const contactItems = [
  {
    icon: Mail,
    label: "kontakt@sf-webseiten.de",
    href: "mailto:kontakt@sf-webseiten.de",
    testId: "contact-email",
  },
  {
    icon: Phone,
    label: "+49 177 4857657",
    href: "tel:+491774857657",
    testId: "contact-phone",
  },
  {
    icon: MapPin,
    label: "Maximilianstraße 57, 86150 Augsburg",
    testId: "contact-location",
  },
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Netlify nimmt Formulare als klassischen Formular-POST auf die eigene
      // Adresse entgegen, nicht als JSON. form-name verweist auf das
      // unsichtbare Formular in public/index.html, das Netlify beim Deploy
      // gefunden hat.
      const daten = new URLSearchParams({
        "form-name": "kontakt",
        name: form.name,
        email: form.email,
        company: form.company,
        message: form.message,
      });
      const antwort = await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: daten.toString(),
      });
      if (!antwort.ok) throw new Error(`Antwort ${antwort.status}`);

      toast.success("Vielen Dank! Wir melden uns innerhalb von 24 Stunden bei Ihnen.");
      setForm({ name: "", email: "", company: "", message: "" });
    } catch (err) {
      toast.error("Etwas hat nicht funktioniert. Bitte versuchen Sie es erneut.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="kontakt" data-testid="contact-section" className="relative z-10 px-6 py-28 md:px-14 md:py-40">
      <div className="mx-auto grid max-w-7xl gap-16 md:grid-cols-2">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-15%" }}
          transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
        >
          <p className="mb-6 text-[11px] tracking-[0.45em] text-[#D4AF37] md:text-sm">KONTAKT · WEBSITE ERSTELLEN LASSEN</p>
          <h2 className="font-display text-4xl font-extrabold uppercase leading-[1.02] tracking-tight sm:text-5xl">
            Ihre neue Website beginnt mit einem{" "}
            <em className="gold-text font-serif-accent normal-case italic">Gespräch.</em>
          </h2>
          <p className="mt-6 max-w-md text-base font-light leading-relaxed text-[#BDBDB6] md:text-lg">
            Sie möchten eine Website erstellen lassen oder Ihren bestehenden Auftritt erneuern?
            Erzählen Sie uns von Ihrem Vorhaben — ob Handwerksbetrieb, Restaurant, Praxis oder
            Kanzlei. Wir antworten in weniger als 24 Stunden, persönlich, ehrlich und ohne
            Verkaufsdruck.
          </p>
          <div className="mt-12 space-y-5">
            {contactItems.map((c) => {
              const Zeile = c.href ? "a" : "div";
              return (
                <Zeile
                  key={c.testId}
                  {...(c.href ? { href: c.href } : {})}
                  className={`flex items-center gap-4 ${
                    c.href ? "transition-colors duration-300 hover:text-[#D4AF37]" : ""
                  }`}
                  data-testid={c.testId}
                >
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#D4AF37]/30 text-[#D4AF37]">
                    <c.icon size={16} />
                  </span>
                  <span className="text-base font-light text-[#F5F5F0]">{c.label}</span>
                </Zeile>
              );
            })}
          </div>
        </motion.div>

        <motion.form
          onSubmit={submit}
          name="kontakt"
          method="POST"
          data-netlify="true"
          netlify-honeypot="bot-field"
          data-testid="contact-form"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-15%" }}
          transition={{ delay: 0.15, duration: 1, ease: [0.16, 1, 0.3, 1] }}
          className="space-y-8 rounded-2xl border border-white/5 bg-[#0d0d0d]/60 p-8 backdrop-blur-xl md:p-10"
        >
          <div className="grid gap-8 sm:grid-cols-2">
            <input
              required
              minLength={2}
              value={form.name}
              onChange={set("name")}
              placeholder="Ihr Name *"
              name="name"
              data-testid="contact-name-input"
              className={inputCls}
            />
            <input
              required
              type="email"
              value={form.email}
              onChange={set("email")}
              placeholder="Ihre E-Mail *"
              name="email"
              data-testid="contact-email-input"
              className={inputCls}
            />
          </div>
          <input
            value={form.company}
            onChange={set("company")}
            placeholder="Unternehmen (optional)"
            name="company"
            data-testid="contact-company-input"
            className={inputCls}
          />
          <textarea
            required
            minLength={10}
            rows={4}
            value={form.message}
            onChange={set("message")}
            placeholder="Worum geht es in Ihrem Projekt? *"
            name="message"
            data-testid="contact-message-input"
            className={`${inputCls} resize-none`}
          />
          <GoldButton type="submit" disabled={loading} testId="contact-submit-button">
            {loading ? "Wird gesendet …" : "Anfrage senden"}
          </GoldButton>
          <p className="text-[11px] font-light leading-relaxed text-[#8A8A84]">
            Mit dem Absenden stimmen Sie zu, dass wir Ihre Angaben zur Beantwortung der Anfrage
            verwenden.
          </p>
        </motion.form>
      </div>
    </section>
  );
}
